import { useState, useEffect } from 'react'
import {
  Box, Typography, Card, CardContent, Button, TextField, Stack,
  Switch, FormControlLabel, Tabs, Tab, Chip, IconButton,
  CircularProgress, Alert, Divider, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper,
} from '@mui/material'
import { Apple, Android, SystemUpdate, Add, Close, Edit, Delete, CalendarMonth, NewReleases } from '@mui/icons-material'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import {
  useAppUpdateConfigs, useUpdateAppConfig, type AppUpdateConfig,
  useAppChangelogs, useUpsertChangelog, useDeleteChangelog, type AppChangelog,
} from '../../hooks/usePlatformAdmin'
import { toast } from '../../store/toastStore'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { AdminDataError } from '../../components/AdminDataError'
import { AdminPageHeader } from '../../components/AdminPageHeader'

// ── Types ────────────────────────────────────────────────────────────

type Platform = 'ios' | 'android'

type MessageLang = 'en' | 'ar' | 'ckb'

const VERSION_PATTERN = /^[vV]?\d+(?:\.\d+){0,2}(?:[-+][0-9A-Za-z.-]+)?$/

function compareVersions(a: string, b: string): number {
  const parts = (value: string) => value.replace(/^[vV]/, '').split(/[-+]/, 1)[0].split('.').map(Number)
  const av = parts(a)
  const bv = parts(b)
  for (let i = 0; i < 3; i += 1) {
    const diff = (av[i] || 0) - (bv[i] || 0)
    if (diff) return diff
  }
  return 0
}

interface PlatformFormState {
  minimum_version: string
  latest_version: string
  store_url: string
  force_update_enabled: boolean
  optional_update_enabled: boolean
  maintenance_mode: boolean
  force_message_en: string
  force_message_ar: string
  force_message_ckb: string
  optional_message_en: string
  optional_message_ar: string
  optional_message_ckb: string
  maintenance_message_en: string
  maintenance_message_ar: string
  maintenance_message_ckb: string
  skip_versions: string[]
  maintenance_starts_at: Date | null
  maintenance_ends_at: Date | null
  force_update_starts_at: Date | null
}

const emptyForm = (): PlatformFormState => ({
  minimum_version: '',
  latest_version: '',
  store_url: '',
  force_update_enabled: false,
  optional_update_enabled: false,
  maintenance_mode: false,
  force_message_en: '',
  force_message_ar: '',
  force_message_ckb: '',
  optional_message_en: '',
  optional_message_ar: '',
  optional_message_ckb: '',
  maintenance_message_en: '',
  maintenance_message_ar: '',
  maintenance_message_ckb: '',
  skip_versions: [],
  maintenance_starts_at: null,
  maintenance_ends_at: null,
  force_update_starts_at: null,
})

function configToForm(config: AppUpdateConfig): PlatformFormState {
  return {
    minimum_version: config.minimum_version,
    latest_version: config.latest_version,
    store_url: config.store_url,
    force_update_enabled: config.force_update_enabled,
    optional_update_enabled: config.optional_update_enabled,
    maintenance_mode: config.maintenance_mode,
    force_message_en: config.force_message_en,
    force_message_ar: config.force_message_ar,
    force_message_ckb: config.force_message_ckb,
    optional_message_en: config.optional_message_en,
    optional_message_ar: config.optional_message_ar,
    optional_message_ckb: config.optional_message_ckb,
    maintenance_message_en: config.maintenance_message_en,
    maintenance_message_ar: config.maintenance_message_ar,
    maintenance_message_ckb: config.maintenance_message_ckb,
    skip_versions: config.skip_versions ?? [],
    maintenance_starts_at: config.maintenance_starts_at ? new Date(config.maintenance_starts_at) : null,
    maintenance_ends_at: config.maintenance_ends_at ? new Date(config.maintenance_ends_at) : null,
    force_update_starts_at: config.force_update_starts_at ? new Date(config.force_update_starts_at) : null,
  }
}

// ── Sub-component: Message tabs ──────────────────────────────────────

interface MessageTabsProps {
  label: string
  fieldPrefix: 'force_message' | 'optional_message' | 'maintenance_message'
  form: PlatformFormState
  onChange: (key: keyof PlatformFormState, value: string) => void
}

function MessageTabs({ label, fieldPrefix, form, onChange }: MessageTabsProps) {
  const [tab, setTab] = useState<MessageLang>('en')

  const langKey = (lang: MessageLang) => `${fieldPrefix}_${lang}` as keyof PlatformFormState

  return (
    <Box>
      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase', mb: 0.5, display: 'block' }}>
        {label}
      </Typography>
      <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1.5, overflow: 'hidden' }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            minHeight: 36,
            bgcolor: '#f8fafc',
            borderBottom: '1px solid #e2e8f0',
            '& .MuiTab-root': { minHeight: 36, fontSize: 12, fontWeight: 600, textTransform: 'none', py: 0.5, px: 2 },
            '& .Mui-selected': { color: '#34A8E0' },
            '& .MuiTabs-indicator': { bgcolor: '#34A8E0' },
          }}
        >
          <Tab label="EN" value="en" />
          <Tab label="AR" value="ar" />
          <Tab label="CKB" value="ckb" />
        </Tabs>
        <TextField
          multiline
          minRows={2}
          fullWidth
          placeholder={`${label} (${tab.toUpperCase()})`}
          value={form[langKey(tab)] as string}
          onChange={(e) => onChange(langKey(tab), e.target.value)}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 0,
              '& fieldset': { border: 'none' },
            },
            '& .MuiInputBase-input': {
              fontSize: 13,
              direction: tab === 'en' ? 'ltr' : 'rtl',
            },
          }}
        />
      </Box>
    </Box>
  )
}

// ── Sub-component: Skip versions chip input ──────────────────────────

interface SkipVersionsInputProps {
  versions: string[]
  onChange: (versions: string[]) => void
}

function SkipVersionsInput({ versions, onChange }: SkipVersionsInputProps) {
  const [input, setInput] = useState('')

  const addVersion = () => {
    const v = input.trim()
    if (!v || versions.includes(v)) return
    onChange([...versions, v])
    setInput('')
  }

  const removeVersion = (v: string) => onChange(versions.filter((x) => x !== v))

  return (
    <Box>
      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase', mb: 0.5, display: 'block' }}>
        Skip Versions
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', gap: 0.75 }}>
        {versions.map((v) => (
          <Chip
            key={v}
            label={v}
            size="small"
            onDelete={() => removeVersion(v)}
            deleteIcon={<Close sx={{ fontSize: '14px !important' }} />}
            sx={{
              bgcolor: '#EBF6FD',
              color: '#2B8BB9',
              border: '1px solid #B3DCF2',
              fontWeight: 600,
              fontSize: 12,
              '& .MuiChip-deleteIcon': { color: '#94a3b8', '&:hover': { color: '#ef4444' } },
            }}
          />
        ))}
        {versions.length === 0 && (
          <Typography variant="caption" color="#cbd5e1" sx={{ lineHeight: '26px' }}>
            No versions skipped
          </Typography>
        )}
      </Stack>
      <Stack direction="row" spacing={1}>
        <TextField
          size="small"
          placeholder="e.g. 2.4.1"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addVersion() } }}
          sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: 13 } }}
        />
        <Tooltip title="Add version">
          <span>
            <IconButton
              size="small"
              onClick={addVersion}
              disabled={!input.trim()}
              sx={{
                bgcolor: input.trim() ? '#34A8E0' : '#f1f5f9',
                color: input.trim() ? '#fff' : '#94a3b8',
                '&:hover': { bgcolor: '#2B8BB9' },
                borderRadius: 1,
              }}
            >
              <Add fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Box>
  )
}

// ── Sub-component: Changelog dialog ──────────────────────────────────

interface ChangelogFormState {
  version: string
  is_critical: boolean
  released_at: Date | null
  title_en: string
  title_ar: string
  title_ckb: string
  body_en: string
  body_ar: string
  body_ckb: string
}

const emptyChangelogForm = (): ChangelogFormState => ({
  version: '',
  is_critical: false,
  released_at: new Date(),
  title_en: '',
  title_ar: '',
  title_ckb: '',
  body_en: '',
  body_ar: '',
  body_ckb: '',
})

function changelogToForm(c: AppChangelog): ChangelogFormState {
  return {
    version: c.version,
    is_critical: c.is_critical,
    released_at: new Date(c.released_at),
    title_en: c.title_en,
    title_ar: c.title_ar,
    title_ckb: c.title_ckb,
    body_en: c.body_en,
    body_ar: c.body_ar,
    body_ckb: c.body_ckb,
  }
}

interface ChangelogDialogProps {
  open: boolean
  platform: Platform
  editing: AppChangelog | null
  onClose: () => void
}

function ChangelogDialog({ open, platform, editing, onClose }: ChangelogDialogProps) {
  const upsert = useUpsertChangelog()
  const [form, setForm] = useState<ChangelogFormState>(emptyChangelogForm())
  const [tab, setTab] = useState<MessageLang>('en')

  useEffect(() => {
    if (open) {
      setForm(editing ? changelogToForm(editing) : emptyChangelogForm())
      setTab('en')
    }
  }, [open, editing])

  const set = <K extends keyof ChangelogFormState>(key: K, value: ChangelogFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }))

  const handleSave = async () => {
    if (!form.version.trim() || !form.released_at) return
    try {
      await upsert.mutateAsync({
        ...(editing ? { id: editing.id } : {}),
        platform,
        version: form.version.trim(),
        is_critical: form.is_critical,
        released_at: form.released_at.toISOString(),
        title_en: form.title_en.trim(),
        title_ar: form.title_ar.trim(),
        title_ckb: form.title_ckb.trim(),
        body_en: form.body_en.trim(),
        body_ar: form.body_ar.trim(),
        body_ckb: form.body_ckb.trim(),
      })
      onClose()
    } catch { /* API interceptor shows the failure */ }
  }

  const titleLangKey = (lang: MessageLang) => `title_${lang}` as keyof ChangelogFormState
  const bodyLangKey = (lang: MessageLang) => `body_${lang}` as keyof ChangelogFormState

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: '#0f172a', pb: 1 }}>
        {editing ? 'Edit Release Notes' : 'Add Release Notes'}
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 2 }}>
        <Stack spacing={2.5}>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              label="Version"
              value={form.version}
              onChange={(e) => set('version', e.target.value)}
              size="small"
              placeholder="2.5.0"
              error={form.version !== '' && !VERSION_PATTERN.test(form.version.trim())}
              helperText={form.version !== '' && !VERSION_PATTERN.test(form.version.trim()) ? 'Use a version such as 2.5.0' : undefined}
              sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: 13 } }}
            />
            <DateTimePicker
              label="Released At"
              value={form.released_at}
              onChange={(val) => set('released_at', val)}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { flex: 1, '& .MuiInputBase-input': { fontSize: 13 } },
                },
              }}
            />
          </Stack>

          <FormControlLabel
            control={
              <Switch
                checked={form.is_critical}
                onChange={(e) => set('is_critical', e.target.checked)}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#dc2626' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#dc2626' },
                }}
              />
            }
            label={<Typography variant="body2" sx={{ fontWeight: 500, color: '#1e293b' }}>Critical Release</Typography>}
          />

          {/* Language tabs for title + body */}
          <Box>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase', mb: 0.5, display: 'block' }}>
              Release Notes Content
            </Typography>
            <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1.5, overflow: 'hidden' }}>
              <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                sx={{
                  minHeight: 36,
                  bgcolor: '#f8fafc',
                  borderBottom: '1px solid #e2e8f0',
                  '& .MuiTab-root': { minHeight: 36, fontSize: 12, fontWeight: 600, textTransform: 'none', py: 0.5, px: 2 },
                  '& .Mui-selected': { color: '#34A8E0' },
                  '& .MuiTabs-indicator': { bgcolor: '#34A8E0' },
                }}
              >
                <Tab label="EN" value="en" />
                <Tab label="AR" value="ar" />
                <Tab label="CKB" value="ckb" />
              </Tabs>
              <Box sx={{ p: 1.5 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={`Title (${tab.toUpperCase()})`}
                  value={form[titleLangKey(tab)] as string}
                  onChange={(e) => set(titleLangKey(tab), e.target.value)}
                  sx={{
                    mb: 1,
                    '& .MuiInputBase-input': { fontSize: 13, direction: tab === 'en' ? 'ltr' : 'rtl' },
                  }}
                />
                <TextField
                  multiline
                  minRows={3}
                  fullWidth
                  placeholder={`Body (${tab.toUpperCase()})`}
                  value={form[bodyLangKey(tab)] as string}
                  onChange={(e) => set(bodyLangKey(tab), e.target.value)}
                  sx={{
                    '& .MuiInputBase-input': { fontSize: 13, direction: tab === 'en' ? 'ltr' : 'rtl' },
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ color: '#64748b', fontWeight: 600 }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={
            upsert.isPending ||
            !VERSION_PATTERN.test(form.version.trim()) ||
            !form.released_at ||
            !form.title_en.trim() ||
            !form.body_en.trim()
          }
          startIcon={upsert.isPending ? <CircularProgress size={14} color="inherit" /> : undefined}
          sx={{
            background: 'linear-gradient(135deg, #34A8E0, #2B8BB9)',
            fontWeight: 600,
            px: 3,
            '&:hover': { background: 'linear-gradient(135deg, #2B8BB9, #1f6f96)' },
            '&.Mui-disabled': { background: '#e2e8f0', color: '#94a3b8' },
          }}
        >
          {upsert.isPending ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ── Sub-component: Changelogs section ────────────────────────────────

interface ChangelogsSectionProps {
  platform: Platform
}

function ChangelogsSection({ platform }: ChangelogsSectionProps) {
  const changelogsQuery = useAppChangelogs(platform)
  const { data: changelogs = [], isLoading } = changelogsQuery
  const deleteChangelog = useDeleteChangelog()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<AppChangelog | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<AppChangelog | null>(null)

  const sorted = [...changelogs].sort(
    (a, b) => new Date(b.released_at).getTime() - new Date(a.released_at).getTime(),
  )

  const handleEdit = (c: AppChangelog) => {
    setEditing(c)
    setDialogOpen(true)
  }

  const handleAdd = () => {
    setEditing(null)
    setDialogOpen(true)
  }

  const handleDelete = (c: AppChangelog) => {
    setDeleteTarget(c)
  }

  return (
    <>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <NewReleases sx={{ fontSize: 16, color: '#34A8E0' }} />
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
              Release Notes
            </Typography>
          </Box>
          <Button
            size="small"
            startIcon={<Add sx={{ fontSize: '15px !important' }} />}
            onClick={handleAdd}
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: '#34A8E0',
              border: '1px solid #B3DCF2',
              bgcolor: '#EBF6FD',
              px: 1.5,
              py: 0.4,
              minHeight: 0,
              '&:hover': { bgcolor: '#d4edf9', borderColor: '#34A8E0' },
            }}
          >
            Add Release Notes
          </Button>
        </Box>

        {changelogsQuery.isError && (
          <AdminDataError message="Release notes could not be loaded." onRetry={() => { void changelogsQuery.refetch() }} />
        )}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={20} sx={{ color: '#34A8E0' }} />
          </Box>
        ) : sorted.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 3, border: '1px dashed #e2e8f0', borderRadius: 1.5 }}>
            <Typography variant="caption" color="#94a3b8">No release notes yet</Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ borderColor: '#e2e8f0', borderRadius: 1.5 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', py: 1 }}>Version</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', py: 1 }}>Title</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', py: 1 }}>Released</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', py: 1 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.map((c) => (
                  <TableRow key={c.id} sx={{ '&:last-child td': { borderBottom: 0 }, '&:hover': { bgcolor: '#f8fafc' } }}>
                    <TableCell sx={{ py: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 12, color: '#0f172a', fontFamily: 'monospace' }}>
                          {c.version}
                        </Typography>
                        {c.is_critical && (
                          <Chip
                            label="Critical"
                            size="small"
                            sx={{ bgcolor: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', fontWeight: 700, fontSize: 10, height: 18 }}
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: 12, color: '#334155' }} noWrap>
                        {c.title_en || <span style={{ color: '#cbd5e1' }}>—</span>}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="caption" sx={{ color: '#64748b', fontSize: 11 }}>
                        {new Date(c.released_at).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }} align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleEdit(c)} sx={{ color: '#64748b', '&:hover': { color: '#34A8E0', bgcolor: '#EBF6FD' } }}>
                            <Edit sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(c)}
                            disabled={deleteChangelog.isPending}
                            sx={{ color: '#64748b', '&:hover': { color: '#dc2626', bgcolor: '#fee2e2' } }}
                          >
                            <Delete sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <ChangelogDialog
        open={dialogOpen}
        platform={platform}
        editing={editing}
        onClose={() => setDialogOpen(false)}
      />
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Release Notes"
        message={`Delete release notes for version ${deleteTarget?.version ?? ''}?`}
        onConfirm={async () => {
          try {
            await deleteChangelog.mutateAsync({ id: deleteTarget!.id, platform })
            setDeleteTarget(null)
          } catch { /* API interceptor shows the failure */ }
        }}
        onClose={() => setDeleteTarget(null)}
        loading={deleteChangelog.isPending}
      />
    </>
  )
}

// ── Sub-component: Scheduled Maintenance pickers ──────────────────────

interface ScheduledMaintenanceProps {
  form: PlatformFormState
  set: (key: keyof PlatformFormState, value: PlatformFormState[keyof PlatformFormState]) => void
}

function ScheduledMaintenance({ form, set }: ScheduledMaintenanceProps) {
  const now = new Date()
  const inWindow =
    form.maintenance_starts_at &&
    form.maintenance_ends_at &&
    now >= form.maintenance_starts_at &&
    now <= form.maintenance_ends_at

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
        <CalendarMonth sx={{ fontSize: 15, color: '#d97706' }} />
        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          Scheduled Maintenance Window
        </Typography>
        {inWindow && (
          <Chip label="Active Now" size="small" sx={{ bgcolor: '#fef3c7', color: '#d97706', border: '1px solid #fde68a', fontWeight: 700, fontSize: 10, height: 18 }} />
        )}
      </Box>

      <Stack spacing={1.5}>
        <DateTimePicker
          label="Maintenance Starts At"
          value={form.maintenance_starts_at}
          onChange={(val) => set('maintenance_starts_at', val)}
          slotProps={{
            textField: {
              size: 'small',
              fullWidth: true,
              helperText: '',
              sx: { '& .MuiInputBase-input': { fontSize: 13 } },
            },
            actionBar: { actions: ['clear', 'accept'] },
          }}
        />
        <DateTimePicker
          label="Maintenance Ends At"
          value={form.maintenance_ends_at}
          onChange={(val) => set('maintenance_ends_at', val)}
          minDateTime={form.maintenance_starts_at ?? undefined}
          slotProps={{
            textField: {
              size: 'small',
              fullWidth: true,
              sx: { '& .MuiInputBase-input': { fontSize: 13 } },
            },
            actionBar: { actions: ['clear', 'accept'] },
          }}
        />
      </Stack>
      <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: 11, mt: 0.75, display: 'block' }}>
        When both are set, maintenance is auto-activated during this window
      </Typography>
    </Box>
  )
}

// ── Sub-component: Force Update Grace Period ──────────────────────────

interface ForceUpdateGracePeriodProps {
  form: PlatformFormState
  set: (key: keyof PlatformFormState, value: PlatformFormState[keyof PlatformFormState]) => void
}

function ForceUpdateGracePeriod({ form, set }: ForceUpdateGracePeriodProps) {
  const now = new Date()
  const isActive = form.force_update_starts_at !== null && now >= form.force_update_starts_at
  const isPending = form.force_update_starts_at !== null && now < form.force_update_starts_at

  let countdownLabel = ''
  if (isPending && form.force_update_starts_at) {
    const diffMs = form.force_update_starts_at.getTime() - now.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))
    if (diffDays > 0) countdownLabel = `Activates in ${diffDays}d ${diffHours}h`
    else if (diffHours > 0) countdownLabel = `Activates in ${diffHours}h ${diffMins}m`
    else countdownLabel = `Activates in ${diffMins}m`
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          Force Update Grace Period
        </Typography>
        {isActive && (
          <Chip label="Force Active" size="small" sx={{ bgcolor: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', fontWeight: 700, fontSize: 10, height: 18 }} />
        )}
        {isPending && countdownLabel && (
          <Chip label={countdownLabel} size="small" sx={{ bgcolor: '#EBF6FD', color: '#34A8E0', border: '1px solid #B3DCF2', fontWeight: 700, fontSize: 10, height: 18 }} />
        )}
      </Box>

      <DateTimePicker
        label="Force Update Starts At"
        value={form.force_update_starts_at}
        onChange={(val) => set('force_update_starts_at', val)}
        slotProps={{
          textField: {
            size: 'small',
            fullWidth: true,
            sx: { '& .MuiInputBase-input': { fontSize: 13 } },
          },
          actionBar: { actions: ['clear', 'accept'] },
        }}
      />
      <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: 11, mt: 0.75, display: 'block' }}>
        Before this date, users see optional update. After, they are forced.
      </Typography>
    </Box>
  )
}

// ── Sub-component: Platform panel ────────────────────────────────────

interface PlatformPanelProps {
  platform: Platform
  config: AppUpdateConfig | undefined
}

function PlatformPanel({ platform, config }: PlatformPanelProps) {
  const updateConfig = useUpdateAppConfig()
  const [form, setForm] = useState<PlatformFormState>(config ? configToForm(config) : emptyForm())

  // Sync when remote data arrives / refreshes
  useEffect(() => {
    if (config) setForm(configToForm(config))
  }, [config])

  const set = (key: keyof PlatformFormState, value: PlatformFormState[keyof PlatformFormState]) =>
    setForm((prev) => ({ ...prev, [key]: value }))

  const validationError = (() => {
    if (!VERSION_PATTERN.test(form.minimum_version.trim())) return 'Minimum version must look like 1.2.3.'
    if (!VERSION_PATTERN.test(form.latest_version.trim())) return 'Latest version must look like 1.2.3.'
    if (compareVersions(form.minimum_version, form.latest_version) > 0) return 'Minimum version cannot be newer than latest version.'
    if (form.store_url && !/^https?:\/\//i.test(form.store_url)) return 'Store URL must start with http:// or https://.'
    if ((form.force_update_enabled || form.optional_update_enabled) && !form.store_url.trim()) return 'A store URL is required when updates are enabled.'
    if (form.force_update_enabled && !form.force_message_en.trim()) return 'Add an English force-update message.'
    if (form.optional_update_enabled && !form.optional_message_en.trim()) return 'Add an English optional-update message.'
    if (form.maintenance_mode && !form.maintenance_message_en.trim()) return 'Add an English maintenance message.'
    if (form.maintenance_starts_at && !form.maintenance_ends_at) return 'Choose a maintenance end time.'
    if (!form.maintenance_starts_at && form.maintenance_ends_at) return 'Choose a maintenance start time.'
    if (form.maintenance_starts_at && form.maintenance_ends_at && form.maintenance_ends_at <= form.maintenance_starts_at) return 'Maintenance must end after it starts.'
    if (form.skip_versions.some((version) => !VERSION_PATTERN.test(version))) return 'Every skip version must be a valid version.'
    return ''
  })()

  const handleSave = async () => {
    try {
      await updateConfig.mutateAsync({
        platform,
        ...form,
        maintenance_starts_at: form.maintenance_starts_at?.toISOString() ?? null,
        maintenance_ends_at: form.maintenance_ends_at?.toISOString() ?? null,
        force_update_starts_at: form.force_update_starts_at?.toISOString() ?? null,
      })
      toast.success(`${platform === 'ios' ? 'iOS' : 'Android'} config saved successfully.`)
    } catch {
      // Error toast shown by API interceptor
    }
  }

  const isIos = platform === 'ios'

  return (
    <>
      <Card sx={{ border: '1px solid #e2e8f0', borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 2,
          borderBottom: '1px solid #f1f5f9',
          background: isIos
            ? 'linear-gradient(135deg, #f8fafc 0%, #EBF6FD 100%)'
            : 'linear-gradient(135deg, #f8fafc 0%, #e8f5e9 100%)',
        }}>
          {isIos
            ? <Apple sx={{ fontSize: 28, color: '#34A8E0' }} />
            : <Android sx={{ fontSize: 28, color: '#4caf50' }} />
          }
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={700} color="#0f172a">
              {isIos ? 'iOS' : 'Android'}
            </Typography>
            {config?.updated_at && (
              <Typography variant="caption" color="#94a3b8">
                Last updated: {new Date(config.updated_at).toLocaleString()}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            {form.maintenance_mode && (
              <Chip label="Maintenance" size="small" sx={{ bgcolor: '#fef3c7', color: '#d97706', border: '1px solid #fde68a', fontWeight: 600, fontSize: 11 }} />
            )}
            {form.force_update_enabled && (
              <Chip label="Force Update" size="small" sx={{ bgcolor: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', fontWeight: 600, fontSize: 11 }} />
            )}
            {form.optional_update_enabled && (
              <Chip label="Optional Update" size="small" sx={{ bgcolor: '#EBF6FD', color: '#34A8E0', border: '1px solid #B3DCF2', fontWeight: 600, fontSize: 11 }} />
            )}
          </Box>
        </Box>

        <CardContent sx={{ flex: 1, p: 2.5 }}>
          <Stack spacing={2.5}>
            {/* Version fields */}
            <Stack direction="row" spacing={2}>
              <TextField
                label="Minimum Version"
                value={form.minimum_version}
                onChange={(e) => set('minimum_version', e.target.value)}
                fullWidth
                size="small"
                placeholder="1.0.0"
                helperText="Oldest supported version"
              />
              <TextField
                label="Latest Version"
                value={form.latest_version}
                onChange={(e) => set('latest_version', e.target.value)}
                fullWidth
                size="small"
                placeholder="2.5.0"
                helperText="Current release version"
              />
            </Stack>

            {/* Store URL */}
            <TextField
              label="Store URL"
              value={form.store_url}
              onChange={(e) => set('store_url', e.target.value)}
              fullWidth
              size="small"
              placeholder={isIos ? 'https://apps.apple.com/app/id...' : 'https://play.google.com/store/apps/details?id=...'}
              helperText={isIos ? 'App Store link' : 'Play Store link'}
            />

            {validationError && <Alert severity="warning">{validationError}</Alert>}

            <Divider sx={{ borderColor: '#f1f5f9' }} />

            {/* Toggle switches */}
            <Box>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase', mb: 1, display: 'block' }}>
                Update Controls
              </Typography>
              <Stack spacing={0.5}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.force_update_enabled}
                      onChange={(e) => set('force_update_enabled', e.target.checked)}
                      size="small"
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#dc2626' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#dc2626' },
                      }}
                    />
                  }
                  label={<Typography variant="body2" sx={{ fontWeight: 500, color: '#1e293b' }}>Force Update Enabled</Typography>}
                />

                {/* Force Update Grace Period — shown below force update toggle */}
                <Box sx={{ pl: 4.5, pb: 1 }}>
                  <ForceUpdateGracePeriod form={form} set={set} />
                </Box>

                <FormControlLabel
                  control={
                    <Switch
                      checked={form.optional_update_enabled}
                      onChange={(e) => set('optional_update_enabled', e.target.checked)}
                      size="small"
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#34A8E0' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#34A8E0' },
                      }}
                    />
                  }
                  label={<Typography variant="body2" sx={{ fontWeight: 500, color: '#1e293b' }}>Optional Update Enabled</Typography>}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.maintenance_mode}
                      onChange={(e) => set('maintenance_mode', e.target.checked)}
                      size="small"
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#d97706' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#d97706' },
                      }}
                    />
                  }
                  label={<Typography variant="body2" sx={{ fontWeight: 500, color: '#1e293b' }}>Maintenance Mode</Typography>}
                />

                {/* Scheduled Maintenance — shown below maintenance toggle */}
                <Box sx={{ pl: 4.5, pb: 1 }}>
                  <ScheduledMaintenance form={form} set={set} />
                </Box>
              </Stack>
            </Box>

            <Divider sx={{ borderColor: '#f1f5f9' }} />

            {/* Multi-language message fields */}
            <Stack spacing={2}>
              <MessageTabs
                label="Force Update Message"
                fieldPrefix="force_message"
                form={form}
                onChange={(k, v) => set(k, v)}
              />
              <MessageTabs
                label="Optional Update Message"
                fieldPrefix="optional_message"
                form={form}
                onChange={(k, v) => set(k, v)}
              />
              <MessageTabs
                label="Maintenance Message"
                fieldPrefix="maintenance_message"
                form={form}
                onChange={(k, v) => set(k, v)}
              />
            </Stack>

            <Divider sx={{ borderColor: '#f1f5f9' }} />

            {/* Skip versions */}
            <SkipVersionsInput
              versions={form.skip_versions}
              onChange={(v) => set('skip_versions', v)}
            />

            <Divider sx={{ borderColor: '#f1f5f9' }} />

            {/* Release Notes / Changelogs */}
            <ChangelogsSection platform={platform} />
          </Stack>
        </CardContent>

        {/* Save footer */}
        <Box sx={{ px: 2.5, py: 2, borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={updateConfig.isPending || !!validationError}
            startIcon={updateConfig.isPending ? <CircularProgress size={14} color="inherit" /> : undefined}
            sx={{
              background: 'linear-gradient(135deg, #34A8E0, #2B8BB9)',
              fontWeight: 600,
              px: 3,
              '&:hover': { background: 'linear-gradient(135deg, #2B8BB9, #1f6f96)' },
              '&.Mui-disabled': { background: '#e2e8f0', color: '#94a3b8' },
            }}
          >
            {updateConfig.isPending ? 'Saving...' : `Save ${isIos ? 'iOS' : 'Android'}`}
          </Button>
        </Box>
      </Card>

    </>
  )
}

// ── Main page ─────────────────────────────────────────────────────────

export default function AdminAppUpdatesPage() {
  const configsQuery = useAppUpdateConfigs()
  const { data: configs = [], isLoading } = configsQuery
  const [selectedPlatform, setSelectedPlatform] = useState<Platform>('ios')

  const iosConfig = configs.find((c) => c.platform === 'ios')
  const androidConfig = configs.find((c) => c.platform === 'android')

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <AdminPageHeader
          eyebrow="Mobile operations"
          title="App Updates"
          description="Manage version requirements, release prompts, maintenance windows, and localized changelogs returned on each client check."
          icon={<SystemUpdate />}
        />

        {configsQuery.isError && (
          <AdminDataError message="App update configuration could not be loaded." onRetry={() => { void configsQuery.refetch() }} />
        )}

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress sx={{ color: '#34A8E0' }} />
          </Box>
        ) : (
          <Box>
            <Card sx={{ mb: 2.5, overflow: 'hidden' }}>
              <Tabs
                value={selectedPlatform}
                onChange={(_, value: Platform) => setSelectedPlatform(value)}
                variant="fullWidth"
                aria-label="Mobile platform settings"
              >
                <Tab value="ios" icon={<Apple />} iconPosition="start" label="iOS" />
                <Tab value="android" icon={<Android />} iconPosition="start" label="Android" />
              </Tabs>
            </Card>
            <Box role="tabpanel" aria-label={`${selectedPlatform} update settings`}>
              {selectedPlatform === 'ios'
                ? <PlatformPanel platform="ios" config={iosConfig} />
                : <PlatformPanel platform="android" config={androidConfig} />}
            </Box>
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  )
}
